import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  useGalleryHeadingColor,
  type GalleryImage,
} from './_galleryShared';

/**
 * Single, unified photo gallery. Pulls every photo from the property's
 * `photo_gallery` collection (no category filter) and lays them out in a
 * square-cropped mosaic — every image is cropped to fill its cell
 * (object-fit: cover) and a repeating subset of cells spans two rows, so the
 * uniform square grid is broken up by taller feature tiles for a staggered,
 * non-uniform pattern. The fixed column count keeps the grid inside its
 * container, so it never overflows horizontally. Clicking any image opens the
 * shared lightbox with keyboard / swipe navigation.
 */
export function Gallery() {
  const heading = useContentToken('gallery.heading');
  const headingColor = useGalleryHeadingColor();

  const allImages = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
    defaultLimit: 48,
  });

  // Exclude non-photo categories (floorplans, logos, and uncategorized
  // "Other" entries) so the gallery only shows actual property photos.
  const images = allImages.filter((img) => {
    const title = (img.title ?? '').trim().toLowerCase();
    return title !== 'floorplan' && title !== 'logo' && title !== 'other';
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-12">
        <EditableToken
          path="gallery.heading"
          as="h2"
          className="gallery-row__heading"
          style={{ color: headingColor }}
        >
          {heading}
        </EditableToken>

        <div className="gallery-grid">
          {images.map((img, index) => {
            // Deterministic mosaic: most tiles are perfect squares, but a
            // repeating subset spans two rows (a tall portrait tile). The mix
            // of square and tall cells produces a staggered, non-uniform
            // pattern while every image is cropped (object-fit: cover) so the
            // grid stays edge-aligned with no awkward gaps.
            const isTall = index % 7 === 2;
            return (
              <button
                key={index}
                type="button"
                className={`gallery-grid__item gallery-item${isTall ? ' gallery-grid__item--tall' : ''}`}
                onClick={() => setSelectedIndex(index)}
                aria-label={`Open image ${index + 1}`}
              >
                <VibesitesImage
                  url={img.image}
                  alt={img.alt_text || img.title || ''}
                  className="gallery-grid__img"
                />
              </button>
            );
          })}
        </div>
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        /* Square-cropped mosaic. A fixed column count keeps the grid inside
           its container (no horizontal overflow), and grid-auto-flow:dense
           backfills the gaps left by the taller feature tiles. */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          grid-auto-flow: dense;
        }
        @media (min-width: 768px) {
          .gallery-grid { grid-template-columns: repeat(3, 1fr); }
        }

        .gallery-grid__item {
          position: relative;
          display: block;
          margin: 0;
          padding: 0;
          border: 0;
          background: none;
          cursor: pointer;
          overflow: hidden;
          border-radius: 3px;
          /* Base tile: a perfect square. In a 1fr column the height resolves
             to the column width, which sizes the implicit grid rows so the
             tall feature tiles below line up exactly two squares high. */
          aspect-ratio: 1 / 1;
        }

        /* Feature tile: one column wide, two rows tall. It inherits its height
           from the two spanned square rows (no aspect-ratio of its own, which
           would fight the gap), giving a tall portrait crop that breaks up the
           uniform grid. */
        .gallery-grid__item--tall {
          grid-row: span 2;
          aspect-ratio: auto;
        }

        .gallery-grid__img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-grid__item:hover .gallery-grid__img {
          transform: scale(1.05);
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-grid__img { transition: none; }
          .gallery-grid__item:hover .gallery-grid__img { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {
    'gallery.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Photo Gallery',
    },
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 48,
      description:
        'Every photo from the property photo gallery, shown in a staggered masonry layout.',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
